import {Icon} from "../common/Icon";
import {useUserContext, logout} from "../../providers/UserContext";
import {ReactNode, useEffect, useState} from "react";
import Dropdown from "../common/Dropdown";
import {CreateGroupModal} from "./Sidebar/CreateGroupModal";
import {Link, useLocation} from "react-router-dom";
import {useInboxContext} from "../../providers/InboxProvider";
import {Button} from "../common/Button";
import {Group, Ping, WorkspaceMember} from "../../lib/types";
import classNames from "classnames";
import {useLayoutContext} from "../../providers/LayoutProvider";
import {formatName} from "../../lib/utils/formatting";

export const Sidebar  = ({}: {}) => {
  const { user, groups, unreadChats, activity, pings, getMember, sidebar } = useUserContext();
  const { unreadCount, setInboxVisible, inboxVisible, inbox } = useInboxContext();
  const [ accountOptionsVisible, setAccountOptionsVisible ] = useState(false);
  const [ modal, setModal ] = useState("");
  const [ unreadPings, setUnreadPings ] = useState(0);
  const [ recentPing, setRecentPing ] = useState<Ping | null>(null);
  const [ lastClosedPing, setLastClosedPing ] = useState<Ping | null>(null);
  const [ dms, setDms ] = useState<Record<string, {
    lastView: number;
    lastMessageTimestamp: number;
    timestamp: number;
    unread: boolean;
    member: WorkspaceMember
  }>>({});
  const {isMobileResolution, isSidebarOpen, setSidebarVisibility} = useLayoutContext();

  useEffect(() => {
    //setUnread and Recent Ping
    if (user && pings) {
      const unread = pings.filter((ping) => ping.timestamp > activity.pings.lastView && ping.authorId !== user.uid);
      setUnreadPings(unread.length);
      const recent = unread[0];
      if(recent && recent.timestamp > activity.pings.lastView && recent.id !== lastClosedPing?.id) {
        setRecentPing(recent);
      } else {
        setRecentPing(null);
      }
    }
  }, [user, pings, activity]);

  /** @todo refactor this to be implemented in the backend and cached */
  useEffect(() => {
    if(inbox) {
      const dms: Record<string, {
        lastView: number;
        lastMessageTimestamp: number;
        timestamp: number;
        unread: boolean;
        member: WorkspaceMember
      }> = {};
      inbox.forEach(chat => {
        const chatUsers = Object.keys(chat.userPermissions || {});
        if(chatUsers.length > 1 && chatUsers.includes(user?.uid || "")) {
          const otherUsers = chatUsers.filter(uid => uid !== user?.uid);
          otherUsers.forEach(uid => {
            const member = getMember(uid);
            if (member && member.id && member.displayName) {
              let dmRecord = {
                lastView: activity.chats[chat.id]?.lastView || 0,
                lastMessageTimestamp: chat.lastMessage?.timestamp || 0,
                timestamp: chat.timestamp,
                unread: !!(chat.lastMessage?.timestamp && chat.lastMessage?.timestamp > (activity.chats[chat.id]?.lastView || 0)),
                member: member
              }
              dmRecord['lastView'] = Math.max(dmRecord['lastView'], dms[uid]?.lastView || 0);
              dmRecord['lastMessageTimestamp'] = Math.max(dmRecord['lastMessageTimestamp'], dms[uid]?.lastMessageTimestamp || 0);
              dmRecord['timestamp'] = Math.max(dmRecord['timestamp'], dms[uid]?.timestamp || 0);
              dmRecord['unread'] = !!(dmRecord['unread'] || dms[uid]?.unread);
              dms[uid] = dmRecord;
            }
          });
        }
      })
      setDms(dms);
    }
  }, [inbox, activity, user]);

  const isGroupUnread = (group: Group) => {
    return unreadChats
       .filter(chat =>
         chat.groupPermissions && chat.groupPermissions[group.id] !== undefined
       ).length
  }
  const groupHasNewPosts = (group: Group) => {
    const lastViewed = activity.groups[group.id]?.lastView || 0;
    return unreadChats
      .filter(chat =>
        chat.groupPermissions && chat.groupPermissions[group.id] !== undefined &&
        !inbox.find(inboxChat => inboxChat.id === chat.id)
      )
      .some(chat => (chat.lastMessage?.timestamp || 0) > lastViewed);
  }

  const [activePage, setActivePage] = useState<string>("");
  const [lastGroup, setLastGroup] = useState<string>("");
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.includes("/inbox")) {
      setActivePage("inbox");
    } else if (location.pathname.includes("/pings")) {
      setActivePage("pings");
    } else if (lastGroup) {
      setActivePage(lastGroup)
    } else {
      setActivePage("");
    }
  }, [inboxVisible, lastGroup]);

  useEffect(() => {
    if (location.pathname.includes("/inbox")) {
      setActivePage("inbox");
    } else if (location.pathname.includes("/pings")) {
      setActivePage("pings");
    } else if (location.pathname.includes("/group/") || location.pathname.includes("/dm/")) {
      const item = location.pathname.split("/")[2];
      setLastGroup(item);
      setActivePage(item);
    } else if (location.pathname.includes("/chats/")) {
      const item = location.pathname.split("/")[2];
      setLastGroup(item);
      setActivePage(item);
    }
    setSidebarVisibility(false);
  }, [location.pathname, location.key])

  const activeClass = "bg-white shadow-elevation-2   text-gray-1"
  const hasMentionsInInbox = unreadChats.some(unreadChat => {
    if (inbox.find(chat => chat.id === unreadChat.id)) {
      return Object.values(unreadChat.messages).some(message => message.mentions && message.mentions.includes(user?.uid || ""));
    }
    return false;
  })

  interface sidebarItem {
    type: "group" | "dm";
    item: Group | {
      lastView: number;
      lastMessageTimestamp: number;
      timestamp: number;
      unread: boolean;
      member: WorkspaceMember;
    }
  }
  const sidebarItems: sidebarItem[] = [];
  sidebarItems.push(...groups.map(group => ({type: "group", item: group})) as sidebarItem[]);
  sidebarItems.push(...Object.values(dms).map(dm => ({type: "dm", item: dm})) as sidebarItem[]);
  sidebarItems.sort((a, b) => {
    return b.item.timestamp - a.item.timestamp;
  });

  return (
    <div className={classNames(
      "flex flex-col gap-2 h-full bg-gray-9 flex-shrink-0 flex-grow-0 overflow-y-auto",
      isMobileResolution
        ? "w-full fixed top-0 z-[999] transition-all duration-300"
        : "w-[12%] min-w-[280px]",
      isMobileResolution && isSidebarOpen && "left-0",
      isMobileResolution && !isSidebarOpen && "-left-full"
    )}
    >
      {isMobileResolution && (
        <button
          onClick={() => setSidebarVisibility(!isSidebarOpen)}
          className={"w-full px-4 py-6 flex items-center gap-2 bg-white shadow-elevation-1"}
        >
          <Icon name={isSidebarOpen ? "close" : "menu"} size="medium"/>
          <span>
            Menu
          </span>
        </button>
      )}
      <div className="px-4 pt-5 flex justify-between items-center">
        <div className="flex gap-2 relative px-2">
          <div
            className="avatar bg-white w-6 h-6 flex justify-center items-center rounded-md font-style-label-sm text-blue-1">
            {getMember(user?.uid || "")?.avatar ?
              (<img src={getMember(user?.uid || "")?.avatar} alt={user?.displayName || ""} className="w-full h-full rounded-md"/>)
              :
              (<span>{user?.displayName?.substring(0,1)}</span>)
            }
          </div>
          <button className="name font-style-body-md text-gray-1" onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setAccountOptionsVisible(!accountOptionsVisible)
          }}>{user?.displayName} <Icon name="keyboard_arrow_down" size="xsmall" /> </button>
          {
            accountOptionsVisible && (
                <Dropdown onClose={() => setAccountOptionsVisible(false)}>
                  <div className="flex flex-col items-start gap-2">
                    <button className="dropdown-option" onClick={()=> logout()}>Logout</button>
                  </div>
                </Dropdown>
            )
          }
        </div>
      </div>
      <div className="px-4">
        <div className="font-style-body-md flex flex-col gap-1 mt-2">
          <Link to="/inbox"
                className={classNames(
                  activePage === "inbox" ? activeClass : "hover:bg-white",
                  "flex items-center gap-2 justify-between rounded-lg p-2 transition-all",
                  unreadCount ? "font-600 text-blue-1" : (
                    activePage === "inbox" ? "text-gray-3 font-500" : "text-gray-4"
                  )
                )}>
            <span className={"flex items-center gap-2"}><Icon name="chat"/> Inbox </span>
            {unreadCount > 0 && (
              <span className={
                classNames(
                  " font-style-label-xxs flex items-center text-gray-1 font-bold",
                )}>{unreadCount}
                {hasMentionsInInbox && (
                  <span
                    className="red-dot rounded-full h-[6px] w-[6px] ml-1 flex justify-center items-center bg-danger-2 text-white"></span>
                )}
              </span>)}
          </Link>
        </div>

        <div className="font-style-body-md flex flex-col gap-1 relative mt-1 hidden">
          <Link
            to="/pings"
            className={classNames(
              "absolute left-[35px] top-[3px] overflow-x-hidden text-white flex-grow  flex justify-between transition-all",
              !recentPing?.id ? "w-0" : "w-[212px]"
            )}>
            <div className={"w-full min-w-[200px] bg-blue-5 flex ml-1 relative rounded-[4px] p-2 font-style-body-sm justify-between"}>
              <div className="triangle absolute top-1/2 -mt-1 rotate-45 -left-1 bg-blue-5 h-2 w-2"></div>
              <div>
                <span className="font-500">{recentPing?.authorName.split(" ")[0]}</span> pinged you
              </div>
              <button className="close" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLastClosedPing(recentPing);
                setRecentPing(null);
              }}>
                <Icon name={"close"} size={"xsmall"}/>
              </button>
            </div>
          </Link>
          <Link to="/pings"
                className={classNames(
                  activePage === "pings" ? activeClass : "hover:bg-white",
                  "flex items-center gap-2 justify-between rounded-lg p-2 transition-all",
                  unreadPings ? "font-600 text-blue-1" : (
                    activePage === "pings" ? "text-gray-3 font-500" : "text-gray-4"
                  )
                )}>
            <span className={"flex items-center gap-2"}><Icon name="campaign"/> Pings </span>
            {unreadPings > 0 && (
              <span className={
                classNames(
                  " font-style-label-xxs flex items-center text-gray-1 font-bold",
                )}>{unreadPings}
              </span>)}
          </Link>
        </div>
      </div>
      <div>
      </div>
      <div className="px-4 pb-2 flex flex-col">
        <CreateGroupModal open={modal === "create-group"} onClose={() => {
          setModal("");
        }}></CreateGroupModal>
        <h3 className="font-style-label-sm text-gray-4 pb-1 px-2">Recipients</h3>
        <div className="font-style-body-md text-gray-2 flex flex-col gap-1 overflow-y-auto h-full w-full overflow-x-hidden">
          {sidebar.map(item => {
            const id = item.id;
            const url = '/chats/' + item.id;
            const unreadCount = unreadChats.filter(chat => chat.sidebarItemId === id).length;
            const hasNewPosts = unreadChats.some(chat => chat.sidebarItemId === id && chat.createdAt > (activity.sidebarItems[id]?.lastView || 0));
            const names: (string|ReactNode)[] = [];
            const icons: ReactNode[] = [];
            if (item.meaningfulGroups) {
              item.meaningfulGroups.forEach(groupId => {
                const group = groups.find(group => group.id === groupId) as Group;
                if (!group) {
                  return;
                }
                if (group) {
                  names.push(<span className={"inline-flex gap-[2px] items-center"}>{group.name}
                    {group.visibility === "private" && (<span className={"w-4 h-4 inline-flex bg-gray-8 items-center justify-center rounded-md"}>
                      <Icon name={"lock"} size={"2xsmall"}/>
                    </span>)}
                  </span>);
                  icons.push(<div>{group.emoji}</div>);
                }
              })
            }
            if (item.meaningfulUsers) {
              item.meaningfulUsers.forEach(uid => {
                const member = getMember(uid);
                if (!member) {
                  return;
                }
                const onlyMe = member.id === user?.uid && item.meaningfulUsers?.length === 1 && !item.meaningfulGroups?.length;
                if (member && (member.id !== user?.uid || onlyMe)) {
                  const name = formatName(member.displayName)
                  const nameHtml: ReactNode = (<span className={"inline-flex gap-[2px] items-center"}>{name}
                    <span className={"w-4 h-4 inline-flex bg-gray-8 items-center justify-center rounded-md"}>
                        <Icon name={"lock"} size={"2xsmall"}/>
                    </span>
                  </span>)
                  names.push(nameHtml);
                  icons.push(<div>{getMember(member.id || "")?.avatar ?
                    (<img src={getMember(member.id || "")?.avatar} alt={member?.displayName || ""}
                          onError={(e) => {e.currentTarget.onerror=null; e.currentTarget.src='/avatar.png'}}
                          className="w-full h-full rounded-md"/>)
                    :
                    (<span>{member?.displayName?.substring(0, 1)}</span>)
                  }</div>);
                }
              })
            }
            return (
              <Link key={id}
                    to={url}
                    className={classNames(
                      activePage === id ? activeClass : "hover:bg-white",
                      "flex items-center gap-2 rounded-lg p-2 transition-all ",
                      unreadCount ? "font-550 text-gray-1" : (
                        activePage === id ? "text-gray-3 font-500" : "text-gray-4"
                      )
                    )}>
                <div className="flex w-full items-center justify-between">
                  <div className={
                    classNames(
                      "icons relative mr-1 h-6 transition-all w-6 group flex-shrink-0",
                      {
                        "hover:w-[24px]": icons.length === 1,
                        "hover:w-[50px]": icons.length === 2,
                        "hover:w-[76px]": icons.length === 3,
                        "hover:w-[104px]": icons.length === 4,
                        "hover:w-[130px]": icons.length >= 5,
                        "opacity-60": unreadCount === 0
                      }
                  )}>
                    {icons.map((icon, index) => (
                      <div key={index} className={
                        classNames("avatar absolute w-6 h-6  transition-all flex justify-center opacity-0 group-hover:opacity-100 items-center rounded-md left-0 top-0 font-style-label-sm text-blue-1 ",
                          index === 0 && "z-10 !opacity-100",
                          index === 1 && "group-hover:left-[26px]",
                          index === 2 && "group-hover:left-[52px]",
                          index === 3 && "group-hover:left-[78px]",
                          index === 4 && "group-hover:left-[106px]",
                        )
                      }>
                        {icon}
                      </div>
                    ))}
                    {icons.length > 1 && (<div className=" transition-all. sicon-counter font-style-body-xs absolute bg-white -bottom-[2px] -right-[2px] rounded-[2px] h-4 w-3 flex justify-center items-center z-10 group-hover:opacity-0">
                      {icons.length}
                    </div>)}
                  </div>
                  <div className="names w-[calc(100%-15px)]  line-clamp-1 break-all font-style-body-md gap-2">
                    {names.map(
                      (name, index) => (
                        <span key={index}>{index > 0 && ", "} {name}</span>
                      )
                    )}
                  </div>
                  {hasNewPosts && (<div className="dot w-1 h-1 rounded-full bg-gray-1"></div>)}

                </div>
              </Link>
            )
          })}
          <button
            className="flex items-center hover:bg-white rounded-lg p-2 transition-all font-style-body-md"
            onClick={() => setModal("create-group")}
          >
            <span className={"ml-[2px] mr-[5px]"}>
              <Icon name={"add"} style={{fontWeight: 600}} size={"small"}/>
            </span>
            <span className="name text-gray-1">Create new group</span>
          </button>
        </div>
      </div>
    </div>
  )
}